import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import SalesChart from "./SalesChart";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Box, Grid, Stack, TextField, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import SalesCategoryChart from "./SalesCategoryChart";
import font from "src/styles/font";
import device from "src/styles/device";

import * as sellerDashboardApi from "src/api/dashboard/sellerPerformanceDashboard";
import moment from "moment";
import { convertIntToCurrency } from "src/common";
import { useSnackbar } from "notistack";

const BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
  /* margin-bottom: 32px; */

  @media ${device.tabletL} {
    padding: 32px 16px;
  }
`;

const PopupContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 16px;
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  overflow: hidden;
  max-width: 90vw;
  max-height: 90vh;
  cursor: pointer;
`;

const PopupImageContainer = styled.div`
  position: relative;
  width: 100%;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10001;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  .MuiSvgIcon-root {
    color: white;
    font-size: 28px;
  }
`;

const DontShowAgainContainer = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  padding: 8px 16px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  z-index: 10001;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
`;

function DashboardSeller() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Popup state
  const [showPopup, setShowPopup] = useState(false);

  const today = new Date();
  const last30Days = new Date();
  last30Days.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(last30Days);
  const [endDate, setEndDate] = useState(today);

  const [salesByCategory, setSalesByCategory] = useState([]);
  const [totalSalesPerDay, setTotalSalesPerDay] = useState([]);

  const [selectedItemCode, setSelectedItemCode] = useState(null);

  const totalValue = salesByCategory.reduce((acc, item) => acc + item.value, 0);

  const handleItemClick = (itemCode) => {
    if (selectedItemCode === itemCode) {
      setSelectedItemCode(null);
      return;
    }
    setSelectedItemCode(itemCode);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const fetchSalesData = async () => {
    try {
      const salesByCategoryData = await sellerDashboardApi.getSalesByItemCode(
        moment(startDate).format("YYYY-MM-DD"),
        moment(endDate).format("YYYY-MM-DD"),
      );
      setSalesByCategory(salesByCategoryData);

      const totalSalesPerDayData = await sellerDashboardApi.getTotalSalesPerDay(
        selectedItemCode,
        moment(startDate).format("YYYY-MM-DD"),
        moment(endDate).format("YYYY-MM-DD"),
      );
      setTotalSalesPerDay(totalSalesPerDayData);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    }
  };

  useEffect(() => {
    fetchSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemCode, startDate, endDate]);

  useEffect(() => {
    setSelectedItemCode(null);
  }, [startDate, endDate]);

  // Popup effect - show popup on first load if not disabled
  useEffect(() => {
    const popupDisabled = localStorage.getItem("aiToolsPopupDisabled");
    if (!popupDisabled) {
      setShowPopup(true);
    }
  }, []);

  // Popup handlers
  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleDontShowAgain = (e) => {
    e.stopPropagation();
    localStorage.setItem("aiToolsPopupDisabled", "true");
    setShowPopup(false);
  };

  const handlePopupClick = () => {
    setShowPopup(false);
    navigate("/product/upload");
  };

  return (
    <>
      {/* AI Tools Popup */}
      {showPopup && (
        <>
          <PopupOverlay onClick={handleClosePopup} />
          <PopupContainer onClick={handlePopupClick}>
            <PopupImageContainer>
              <img
                src="https://xsurface-images.s3.ap-southeast-1.amazonaws.com/Popup-Ai-seller.jpg"
                alt="AI Tools Feature"
                style={{ maxWidth: "600px", width: "100%" }}
              />
              <CloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleClosePopup();
                }}
              >
                <CloseIcon />
              </CloseButton>
              <DontShowAgainContainer onClick={handleDontShowAgain}>
                ไม่แสดงอีก
              </DontShowAgainContainer>
            </PopupImageContainer>
          </PopupContainer>
        </>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <BoxWrapper>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                gap: 2,
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: {
                    xs: "center",
                    sm: "flex-start",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    fontSize: {
                      xs: `${font.size.lg} !important`,
                      sm: `${font.size.xl} !important`,
                    },
                  }}
                >
                  ภาพรวมยอดขาย
                </Typography>
                <Typography
                  sx={{
                    color: "#f44336",
                    fontWeight: 700,
                    fontSize: {
                      xs: `2rem !important`,
                    },
                    whiteSpace: "nowrap",
                  }}
                  variant="h4"
                >
                  ฿ {convertIntToCurrency(totalValue)}
                </Typography>
              </Stack>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <DatePicker
                    value={startDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      // Check date difference not more than 1 year
                      const _startDate = dayjs(newValue);
                      const _endDate = dayjs(endDate);
                      const diff = _endDate.diff(_startDate, "day");
                      if (diff > 365) {
                        enqueueSnackbar(
                          "ไม่สามารถเลือกช่วงเวลามากกว่า 1 ปีได้",
                          {
                            variant: "error",
                          },
                        );
                        return;
                      }
                      const formattedDate = dayjs(newValue).isValid()
                        ? dayjs(newValue).format("YYYY-MM-DD")
                        : null;
                      setStartDate(formattedDate);
                    }}
                    maxDate={dayjs(new Date(endDate))}
                    renderInput={(params) => (
                      <TextField
                        onKeyDown={onKeyDown}
                        {...params}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        sx={{
                          mx: 1,
                          width: "100%",
                          maxWidth: 200,
                          background: "#fff",
                          ".MuiOutlinedInput-root": {
                            paddingRight: "18px",
                          },
                        }}
                        label="ตั้งแต่"
                        size="small"
                      />
                    )}
                  />
                  -
                  <DatePicker
                    value={endDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      const _startDate = dayjs(startDate);
                      const _endDate = dayjs(newValue);
                      const diff = _endDate.diff(_startDate, "day");
                      if (diff > 365) {
                        enqueueSnackbar(
                          "ไม่สามารถเลือกช่วงเวลามากกว่า 1 ปีได้",
                          {
                            variant: "error",
                          },
                        );
                        return;
                      }
                      const formattedDate = dayjs(newValue).isValid()
                        ? dayjs(newValue).format("YYYY-MM-DD")
                        : null;
                      setEndDate(formattedDate);
                    }}
                    minDate={dayjs(new Date(startDate))}
                    maxDate={dayjs(new Date())}
                    renderInput={(params) => (
                      <TextField
                        onKeyDown={onKeyDown}
                        {...params}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        sx={{
                          ml: 1,
                          width: "100%",
                          maxWidth: 200,
                          background: "#fff",
                          ".MuiOutlinedInput-root": {
                            paddingRight: "18px",
                          },
                        }}
                        label="ถึง"
                        size="small"
                      />
                    )}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </BoxWrapper>
        </Grid>
        <Grid item xs={12}>
          <BoxWrapper>
            <SalesCategoryChart
              data={salesByCategory}
              selectedItemCode={selectedItemCode}
              onItemClick={handleItemClick}
            />
          </BoxWrapper>
        </Grid>
        <Grid item xs={12}>
          <BoxWrapper>
            <SalesChart
              data={totalSalesPerDay}
              selectedItemCode={selectedItemCode}
            />
          </BoxWrapper>
        </Grid>
      </Grid>
    </>
  );
}

export default DashboardSeller;
